import { type Discount } from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { DeepPartial } from '~/types';
import computeDiscountTotals from '~/utilities/vdp/computeDiscountTotals';

export const defaultContext = {
  discounts: defaultLoadableState<Discount[]>([]),
  fetchDiscountsFlag: 0,
  hasDiscount: false,
  isInExcludedState: false,
  totalDiscount: 0,
  totalSiteWideDiscount: 0,
  tradeIncentive: 0,
  triggerFetchDiscounts: () => {}
};

export type ContextType = typeof defaultContext;

const DiscountContext = createContext<ContextType>(defaultContext);

type OwnProps = React.PropsWithChildren<{
  hasActiveTrade: boolean;
  isTradeInApplied: boolean;
}>;

export function DiscountContextProvider({
  children,
  hasActiveTrade,
  isTradeInApplied
}: OwnProps) {
  const discounts = useLoadableState<Discount[]>([]);
  const [fetchDiscountsFlag, setFetchDiscountsFlag] = useState(
    defaultContext.fetchDiscountsFlag
  );

  const triggerFetchDiscounts = useCallback(
    () => setFetchDiscountsFlag(current => current + 1),
    []
  );

  const value = useMemo<ContextType>(() => {
    const {
      isInExcludedState,
      totalDiscount,
      totalSiteWideDiscount,
      tradeIncentive
    } = computeDiscountTotals(discounts.data, {
      hasActiveTrade,
      isTradeInApplied
    });

    return {
      discounts,
      fetchDiscountsFlag,
      hasDiscount: !isInExcludedState && totalDiscount > 0,
      isInExcludedState,
      totalDiscount,
      totalSiteWideDiscount,
      tradeIncentive,
      triggerFetchDiscounts
    };
  }, [
    discounts,
    fetchDiscountsFlag,
    hasActiveTrade,
    isTradeInApplied,
    triggerFetchDiscounts
  ]);

  return (
    <DiscountContext.Provider value={value}>
      {children}
    </DiscountContext.Provider>
  );
}

DiscountContextProvider.displayName = 'DiscountContextProvider';

export const generateDiscountContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useDiscounts() {
  return useContext(DiscountContext).discounts.data;
}

export function useIsDiscountsLoaded() {
  return useContext(DiscountContext).discounts.isLoaded;
}

export default DiscountContext;
